import { mirageJS } from '@Green-Dot-Corporation/eureka-lib-mock-utils';
import { getApiUrlWithEnv } from '../utils/serviceUtils';
import { HEADERS_AND_PARAMS, ACTIVATE_CARD } from '../const/apis';

const { Response } = mirageJS;
const REDIRECT_URLS = {
    successUrl: 'https://partner.com/success-return-url',
    errorUrl: 'https://partner.com/error-return-url',
};

// 401
const notAuthorizedResp = (options) => {
    const { message, body = {} } = options ?? {};

    return new Response(
        401,
        {
            header: {
                ResponseId: 'ce29a580-2240-11e6-9eb5-0002a5d5c523',
                StatusCode: '401',
                SubStatusCode: '40001',
                Message: message || 'not authorized',
            },
            ...REDIRECT_URLS,
            ...body,
        },
        {},
    );
};

// 403
const forbiddenResp = (options) => {
    const { message, body = {} } = options ?? {};

    return new Response(403, {
        header: {
            ResponseId: 'ce29a580-2240-11e6-9eb5-0002a5d5c523',
            StatusCode: '403',
            SubStatusCode: '40003',
            Message: message || 'forbidden',
        },
        ...REDIRECT_URLS,
        ...body,
    });
};

// 404
const notFoundResp = (options) => {
    const { message, body = {} } = options ?? {};

    return new Response(
        404,
        {
            header: {
                ResponseId: 'ce29a580-2240-11e6-9eb5-0002a5d5c523',
                StatusCode: '404',
                SubStatusCode: '40009',
                Message: message || 'not found',
            },
            ...REDIRECT_URLS,
            ...body,
        },
        {},
    );
};

// 412
const preconditionFailResp = (options) => {
    const { message, body = {} } = options ?? {};

    return new Response(
        412,
        {
            header: {
                ResponseId: 'ce29a580-2240-11e6-9eb5-0002a5d5c523',
                StatusCode: '',
                SubStatusCode: '',
                Message: message || 'request pre-condition check fail',
            },
            ...REDIRECT_URLS,
            ...body,
        },
        {},
    );
};

// 500
const internalErrorResp = (options) => {
    const { message, body = {} } = options ?? {};

    return new Response(
        500,
        {
            header: {
                ResponseId: 'ce29a580-2240-11e6-9eb5-0002a5d5c523',
                StatusCode: '502',
                SubStatusCode: '50001',
                Message: message || 'internal error',
            },
            ...REDIRECT_URLS,
            ...body,
        },
        {},
    );
};

function getRequestHeaders(headers) {
    const {
        [HEADERS_AND_PARAMS.AUTHORIZATION.toLowerCase()]: authorization,
        [HEADERS_AND_PARAMS.PROGRAM_CODE.toLowerCase()]: programCode,
        [HEADERS_AND_PARAMS.REQUEST_ID.toLowerCase()]: requestId,
        [HEADERS_AND_PARAMS.CODE.toLowerCase()]: code,
        [HEADERS_AND_PARAMS.SIGN.toLowerCase()]: sign,
    } = headers;

    return { authorization, programCode, requestId, code, sign };
}

const getCardInfo = (request) => {
    const { params, requestHeaders } = request ?? {};
    const { [HEADERS_AND_PARAMS.ACCOUNT_IDENTIFIER]: accountIdentifier } =
        params ?? {};

    const { authorization, programCode, requestId, code, sign } =
        getRequestHeaders(requestHeaders);

    if (
        !accountIdentifier ||
        !requestId ||
        !programCode ||
        !authorization ||
        !code ||
        !sign
    ) {
        return preconditionFailResp();
    }

    const { ACTIVATED_STATUS, NOT_ACTIVATED_STATUS } = ACTIVATE_CARD;

    const activationStatus =
        requestId === '300' ? ACTIVATED_STATUS : NOT_ACTIVATED_STATUS;
    const isPinSet = requestId === '600';

    switch (requestId) {
        case '401':
            return notAuthorizedResp();
        case '404':
            return notFoundResp();
        case '403':
            return forbiddenResp();
        case '500':
            return internalErrorResp();
        default:
            return {
                header: { message: '', statuscode: '' },
                paymentInstrument: {
                    paymentIdentifier: '5629a580-2240-11e6-9eb5-0002a5d5c578',
                    paymentInstrumentIdentifier:
                        '4529a580-2240-11e6-9eb5-0002a5d5c512',
                    paymentInstrumentType: 'GPR',
                    status: activationStatus,
                    isPinSet,
                    last4Pan: '9527',
                    cardNumber: '4133310000442900',
                    expDate: '07/2023',
                    cvv: '123',
                    embossedName: 'Dylan Jones',
                    IsOverIssuedDaysLimit: code === '3333',
                },
                ...REDIRECT_URLS,
            };
    }
};

const activateCard = (request) => {
    const { requestHeaders, requestBody } = request ?? {};
    const { cvv } = JSON.parse(requestBody);

    const { [HEADERS_AND_PARAMS.AUTHORIZATION.toLowerCase()]: authorization } =
        requestHeaders;
    const { programCode, requestId, code, sign } =
        getRequestHeaders(requestHeaders);

    if (!authorization || !requestId || !programCode || !code || !sign) {
        return preconditionFailResp();
    }

    switch (cvv) {
        case '401':
            return notAuthorizedResp();
        case '404':
            return notFoundResp();
        case '403':
            return forbiddenResp();
        case '500':
            return internalErrorResp();
        default:
            return {
                paymentInstrumentIdentifier:
                    'cd29a580-2240-11e6-9eb5-0002a5d5c5cb',
                activationStatus: 'activated',
                header: { message: '', statuscode: '' },
                ...REDIRECT_URLS,
            };
    }
};

const setPin = (request) => {
    const { requestBody } = request ?? {};
    const { atmPin } = JSON.parse(requestBody);

    switch (atmPin) {
        case '4401':
            return notAuthorizedResp('not authorized');
        case '4404':
            return notFoundResp('not found');
        case '4412':
            return preconditionFailResp('request pre-condition check fail');
        case '5500':
            return internalErrorResp('internal error');
        default:
            return REDIRECT_URLS;
    }
};

function getAccessToken(request) {
    const { params, requestHeaders } = request ?? {};
    const { [HEADERS_AND_PARAMS.ACCOUNT_IDENTIFIER]: accountIdentifier } =
        params ?? {};
    const { programCode, requestId, code } = getRequestHeaders(requestHeaders);

    const hasRequiredParams = [
        code,
        accountIdentifier,
        requestId,
        programCode,
    ].every((param) => {
        return !!param && param !== 'null';
    });

    if (!hasRequiredParams) {
        return preconditionFailResp();
    }

    switch (code) {
        case '401':
            return notAuthorizedResp();
        case '404':
            return notFoundResp();
        case '403':
            return forbiddenResp();
        case '500':
            return internalErrorResp();
        default:
            return {
                Token: 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJzdWIiOiIxMjM0NTY3ODkwIiwibmFtZSI6IkpvaG4gRG9lIiwiaWF0IjoxNTE2MjM5MDIyfQ.SflKxwRJSMeKKF2QT4fwpMeJf36POk6yJV_adQssw5c',
            };
    }
}

const validateCard = (request) => {
    const { requestHeaders, requestBody } = request ?? {};
    const { cvv } = JSON.parse(requestBody);

    const { [HEADERS_AND_PARAMS.AUTHORIZATION.toLowerCase()]: authorization } =
        requestHeaders;
    const { programCode, requestId, code, sign } =
        getRequestHeaders(requestHeaders);

    if (!authorization || !requestId || !programCode || !code || !sign) {
        return preconditionFailResp();
    }

    switch (cvv) {
        case '401':
            return notAuthorizedResp();
        case '404':
            return notFoundResp();
        case '403':
            return forbiddenResp();
        case '500':
            return internalErrorResp();
        default:
            return {
                success: true,
                header: { message: '', statuscode: '' },
                ...REDIRECT_URLS,
            };
    }
};

export function registerCardMgmtRoutes(server) {
    const serverConfig = server;

    return [
        serverConfig.get(
            `${getApiUrlWithEnv()}/api/:accountIdentifier/token`,
            (schema, request) => getAccessToken(request),
        ),
        serverConfig.get(
            `${getApiUrlWithEnv()}/api/cards/:accountIdentifier`,
            (schema, request) => getCardInfo(request),
        ),
        serverConfig.put(
            `${getApiUrlWithEnv()}/api/cards/activate`,
            (schema, request) => activateCard(request),
        ),
        serverConfig.put(
            `${getApiUrlWithEnv()}/api/cards/atmpin`,
            (schema, request) => setPin(request),
        ),
        serverConfig.post(
            `${getApiUrlWithEnv()}/api/cards/validate`,
            (schema, request) => validateCard(request),
        ),
    ];
}

export default {};
